// src/components/ui/SudsInput.tsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import type { SudsControlConfig } from "../../types/chat";

export type SudsInputSource =
    | "slider"
    | "gauge"
    | "emoji";

type Props = {
    control: SudsControlConfig;
    disabled?: boolean;
    onSubmit: (
        value: number,
        source: SudsInputSource
    ) => void | Promise<void>;
};

const EMOJI_SUDS_OPTIONS = [
    {
        value: 2,
        label: "Low distress",
        image: "/assets/suds/suds-low.png",
        alt: "Calm face representing low distress",
        toneClass:
            "bg-[linear-gradient(180deg,#fff_0%,#54a93622_100%)]",
        interactionToneClass:
            "hover:bg-[linear-gradient(180deg,#fff_0%,#54a93622_100%)] focus-visible:bg-[linear-gradient(180deg,#fff_0%,#54a93622_100%)]",
    },
    {
        value: 5,
        label: "Moderate distress",
        image: "/assets/suds/suds-moderate.png",
        alt: "Worried face representing moderate distress",
        toneClass:
            "bg-[linear-gradient(180deg,#fff_0%,#f0a21c22_100%)]",
        interactionToneClass:
            "hover:bg-[linear-gradient(180deg,#fff_0%,#f0a21c22_100%)] focus-visible:bg-[linear-gradient(180deg,#fff_0%,#f0a21c22_100%)]",
    },
    {
        value: 8,
        label: "High distress",
        image: "/assets/suds/suds-high.png",
        alt: "Very distressed face representing high distress",
        toneClass:
            "bg-[linear-gradient(180deg,#fff_0%,#e33b2f22_100%)]",
        interactionToneClass:
            "hover:bg-[linear-gradient(180deg,#fff_0%,#e33b2f22_100%)] focus-visible:bg-[linear-gradient(180deg,#fff_0%,#e33b2f22_100%)]",
    },
] as const;

const GAUGE_WIDTH = 320;
const GAUGE_HEIGHT = 190;
const GAUGE_CX = 160;
const GAUGE_CY = 150;
const GAUGE_RADIUS = 108;

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const roundToStep = (
    value: number,
    min: number,
    max: number,
    step: number
) => {
    const safeStep = step > 0 ? step : 1;

    const stepsFromMin = Math.round((value - min) / safeStep);
    const stepped = min + stepsFromMin * safeStep;

    return clamp(stepped, min, max);
};

const formatValue = (value: number) => {
    if (Number.isInteger(value)) return String(value);

    return String(Number(value.toFixed(2)));
};

const pointOnGauge = (
    angleDegrees: number,
    radius: number
) => {
    const radians = (angleDegrees * Math.PI) / 180;

    return {
        x: GAUGE_CX + radius * Math.cos(radians),
        y: GAUGE_CY - radius * Math.sin(radians),
    };
};

const arcPath = (
    startAngle: number,
    endAngle: number,
    radius: number
) => {
    const start = pointOnGauge(startAngle, radius);
    const end = pointOnGauge(endAngle, radius);

    const largeArcFlag =
        Math.abs(startAngle - endAngle) > 180 ? 1 : 0;

    return [
        `M ${start.x} ${start.y}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    ].join(" ");
};

const SudsInput: React.FC<Props> = ({
    control,
    disabled = false,
    onSubmit,
}) => {
    const min = Number.isFinite(control.min) ? control.min : 0;
    const max =
        Number.isFinite(control.max) && control.max > min
            ? control.max
            : 10;

    const step =
        Number.isFinite(control.step) && control.step > 0
            ? control.step
            : 1;

    const midpoint = useMemo(
        () =>
            roundToStep(
                min + (max - min) / 2,
                min,
                max,
                step
            ),
        [min, max, step]
    );

    // Start Slider and Gauge at the midpoint (5 for a 0–10 scale).
    const [selectedValue, setSelectedValue] =
        useState<number>(midpoint);

    // Emoji starts with no choice selected.
    // The client must deliberately choose Low, Moderate, or High.
    const [selectedEmojiValue, setSelectedEmojiValue] =
        useState<number | null>(null);

    const [submitted, setSubmitted] = useState(false);

    const draggingRef = useRef(false);

    // If a new SUDS prompt replaces the old one,
    // do not carry over the previous local selection.
    useEffect(() => {
        setSelectedValue(midpoint);
        setSelectedEmojiValue(null);
        setSubmitted(false);
        draggingRef.current = false;
    }, [
        control.style,
        control.stage,
        control.awaitKey,
        min,
        max,
        step,
        midpoint,
    ]);

    const selectValue = useCallback(
        (rawValue: number) => {
            if (disabled || submitted) return;

            setSelectedValue(
                roundToStep(rawValue, min, max, step)
            );
        },
        [disabled, submitted, min, max, step]
    );

    const submit = useCallback(
        async (
            source: SudsInputSource,
            valueOverride?: number
        ) => {
            if (disabled || submitted) {
                return;
            }

            const valueToSubmit =
                valueOverride ?? selectedValue;

            setSubmitted(true);

            try {
                await onSubmit(
                    valueToSubmit,
                    source
                );
            } catch (error) {
                // Allow retry if the parent submission fails.
                setSubmitted(false);
                throw error;
            }
        },
        [
            disabled,
            submitted,
            selectedValue,
            onSubmit,
        ]
    );

    // Existing QuickActionsBar continues to own
    // the "Number Buttons" presentation.
    if (control.style === "buttons") {
        return null;
    }
    // ─────────────────────────────────────────────
    // Emoji
    // ─────────────────────────────────────────────
    if (String(control.style) === "emoji") {
        const selectedEmoji =
            EMOJI_SUDS_OPTIONS.find(
                (option) =>
                    option.value === selectedEmojiValue
            ) ?? null;

        return (
            <div className="mt-3 w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div>
                    <div className="text-sm font-semibold text-slate-900">
                        SUDS rating
                    </div>

                    <div className="mt-1 text-sm text-slate-500">
                        Tap the <strong>face</strong> that best matches how distressed you feel.
                    </div>
                </div>

                <div
                    className="mt-5 grid grid-cols-3 gap-2 sm:gap-3"
                    role="group"
                    aria-label="Choose your distress level"
                >
                    {EMOJI_SUDS_OPTIONS.map((option) => {
                        const isSelected =
                            selectedEmojiValue === option.value;

                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={disabled || submitted}
                                aria-pressed={isSelected}
                                onClick={() => {
                                    if (disabled || submitted) {
                                        return;
                                    }

                                    setSelectedEmojiValue(
                                        option.value
                                    );
                                }}
                                className={[
                                    "flex min-w-0 flex-col items-center rounded-2xl border p-2.5 transition-all sm:p-3",
                                    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                                    "disabled:cursor-not-allowed disabled:opacity-60",
                                    option.interactionToneClass,
                                    isSelected
                                        ? `border-blue-600 ${option.toneClass} ring-2 ring-blue-600`
                                        : "border-slate-200 bg-white hover:border-blue-300",
                                ].join(" ")}
                            >
                                <img
                                    src={option.image}
                                    alt={option.alt}
                                    draggable={false}
                                    className={[
                                        "h-20 sm:h-24",
                                        option.value === 8
                                            ? "w-full object-contain"
                                            : "w-20 rounded-full object-cover sm:w-24",
                                    ].join(" ")}
                                />

                                <span
                                    className={[
                                        "mt-2 text-center text-sm font-semibold leading-tight",
                                        isSelected
                                            ? "text-blue-700"
                                            : "text-slate-700",
                                    ].join(" ")}
                                >
                                    {option.label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    disabled={
                        disabled ||
                        submitted ||
                        selectedEmojiValue === null
                    }
                    onClick={() => {
                        if (selectedEmojiValue === null) {
                            return;
                        }

                        void submit(
                            "emoji",
                            selectedEmojiValue
                        );
                    }}
                    className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                    {submitted
                        ? "Submitting…"
                        : selectedEmoji
                            ? `Submit ${selectedEmoji.label}`
                            : "Select a face"}
                </button>
            </div>
        );
    }

    const selectedLabel = formatValue(selectedValue);

    // ─────────────────────────────────────────────
    // Slider
    // ─────────────────────────────────────────────
    if (control.style === "slider") {
        const sliderValue = selectedValue;

        const sliderPercent = clamp(
            ((sliderValue - min) / (max - min)) * 100,
            0,
            100
        );

        return (
            <div className="mt-3 w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">
                            SUDS rating
                        </div>

                        <div className="mt-1 text-sm text-slate-500">
                            Tap or drag the slider to choose a rating.
                        </div>
                    </div>

                    <div
                        className="min-w-12 text-center text-3xl font-bold tabular-nums text-slate-900"
                        aria-live="polite"
                    >
                        {selectedLabel}
                    </div>
                </div>

                <div className="mt-5">
                    <div className="relative flex h-7 items-center">
                        {/* Visual SUDS spectrum track */}
                        <div className="pointer-events-none absolute inset-x-0 h-2 overflow-hidden rounded-full bg-slate-200">
                            {/* Full low → high distress spectrum */}
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(to right, #22c55e 0%, #84cc16 30%, #facc15 50%, #fb923c 70%, #ef4444 100%)",
                                }}
                            />

                            {/* Hide the portion to the right of the current value */}
                            <div
                                className="absolute inset-y-0 right-0 bg-slate-200"
                                style={{
                                    left: `${sliderPercent}%`,
                                }}
                            />
                        </div>

                        {/* Actual accessible range control */}
                        <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={sliderValue}
                            disabled={disabled || submitted}
                            aria-label="SUDS rating"
                            aria-valuemin={min}
                            aria-valuemax={max}
                            aria-valuenow={selectedValue}
                            onChange={(event) => {
                                selectValue(Number(event.target.value));
                            }}
                            className={[
                                "relative z-10 h-7 w-full cursor-pointer appearance-none bg-transparent",
                                "disabled:cursor-not-allowed disabled:opacity-60",

                                // Chrome / Safari / iPhone
                                "[&::-webkit-slider-runnable-track]:h-2",
                                "[&::-webkit-slider-runnable-track]:rounded-full",
                                "[&::-webkit-slider-runnable-track]:bg-transparent",

                                // Larger, high-contrast blue thumb with a white ring
                                "[&::-webkit-slider-thumb]:mt-[-12px]",
                                "[&::-webkit-slider-thumb]:h-8",
                                "[&::-webkit-slider-thumb]:w-8",
                                "[&::-webkit-slider-thumb]:appearance-none",
                                "[&::-webkit-slider-thumb]:rounded-full",
                                "[&::-webkit-slider-thumb]:border-[3px]",
                                "[&::-webkit-slider-thumb]:border-solid",
                                "[&::-webkit-slider-thumb]:border-white",
                                "[&::-webkit-slider-thumb]:bg-blue-600",
                                "[&::-webkit-slider-thumb]:shadow-[0_0_0_1px_#94a3b8,0_4px_10px_rgba(15,23,42,0.20)]",

                                // Firefox
                                "[&::-moz-range-track]:h-2",
                                "[&::-moz-range-track]:rounded-full",
                                "[&::-moz-range-track]:bg-transparent",
                                "[&::-moz-range-progress]:bg-transparent",
                                "[&::-moz-range-thumb]:h-8",
                                "[&::-moz-range-thumb]:w-8",
                                "[&::-moz-range-thumb]:rounded-full",
                                "[&::-moz-range-thumb]:border-[3px]",
                                "[&::-moz-range-thumb]:border-solid",
                                "[&::-moz-range-thumb]:border-white",
                                "[&::-moz-range-thumb]:bg-blue-600",
                                "[&::-moz-range-thumb]:shadow-[0_0_0_1px_#94a3b8,0_4px_10px_rgba(15,23,42,0.20)]",
                            ].join(" ")}
                        />
                    </div>

                    <div className="mt-1 flex justify-between text-xs font-medium text-slate-500">
                        <span>{formatValue(min)}</span>
                        <span>{formatValue(max)}</span>
                    </div>

                    <div className="mt-3 flex justify-between text-sm text-slate-500">
                        <span>No distress</span>
                        <span>Extreme distress</span>
                    </div>
                </div>

                <button
                    type="button"
                    disabled={disabled || submitted}
                    onClick={() => void submit("slider")}
                    className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                    {submitted
                        ? "Submitting…"
                        : `Submit ${formatValue(selectedValue)}`}
                </button>
            </div>
        );
    }

    // ─────────────────────────────────────────────
    // Gauge
    // ─────────────────────────────────────────────

    const fraction =
        (selectedValue - min) / (max - min);

    // left = 180°, right = 0°
    const needleAngle =
        180 - clamp(fraction, 0, 1) * 180;

    const needleTip =
        pointOnGauge(needleAngle, 82);

    const updateGaugeFromPointer = (
        event: React.PointerEvent<SVGSVGElement>
    ) => {
        if (disabled || submitted) return;

        const rect =
            event.currentTarget.getBoundingClientRect();

        if (!rect.width || !rect.height) return;

        const x =
            ((event.clientX - rect.left) / rect.width) *
            GAUGE_WIDTH;

        const y =
            ((event.clientY - rect.top) / rect.height) *
            GAUGE_HEIGHT;

        const dx = x - GAUGE_CX;
        const dy = GAUGE_CY - y;

        let angle =
            (Math.atan2(dy, dx) * 180) / Math.PI;

        angle = clamp(angle, 0, 180);

        const nextFraction =
            (180 - angle) / 180;

        const rawValue =
            min + nextFraction * (max - min);

        selectValue(rawValue);
    };

    const handleGaugeKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (disabled || submitted) return;

        const current = selectedValue;

        let next: number | null = null;

        switch (event.key) {
            case "ArrowRight":
            case "ArrowUp":
                next = current + step;
                break;

            case "ArrowLeft":
            case "ArrowDown":
                next = current - step;
                break;

            case "Home":
                next = min;
                break;

            case "End":
                next = max;
                break;

            default:
                return;
        }

        event.preventDefault();
        selectValue(next);
    };

    // Five visual bands similar to the gauge concept:
    // low → moderate → high.
    const gaugeBands = [
        {
            from: 180,
            to: 144,
            stroke: "#22c55e",
        },
        {
            from: 144,
            to: 108,
            stroke: "#84cc16",
        },
        {
            from: 108,
            to: 72,
            stroke: "#facc15",
        },
        {
            from: 72,
            to: 36,
            stroke: "#fb923c",
        },
        {
            from: 36,
            to: 0,
            stroke: "#ef4444",
        },
    ];

    const tickValues = Array.from(
        { length: 11 },
        (_, index) =>
            min + ((max - min) * index) / 10
    );

    return (
        <div className="mt-3 w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-sm font-semibold text-slate-900">
                        SUDS rating
                    </div>

                    <div className="mt-1 text-sm text-slate-500">
                        Tap or drag the gauge to choose a rating.
                    </div>
                </div>

                <div
                    className="min-w-12 text-center text-3xl font-bold tabular-nums text-slate-900"
                    aria-live="polite"
                >
                    {selectedLabel}
                </div>
            </div>

            <div
                role="slider"
                tabIndex={
                    disabled || submitted ? -1 : 0
                }
                aria-label="SUDS rating"
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={selectedValue}
                aria-valuetext={`${formatValue(selectedValue)} out of ${formatValue(max)}`}
                onKeyDown={handleGaugeKeyDown}
                className="mt-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
                <svg
                    viewBox={`0 0 ${GAUGE_WIDTH} ${GAUGE_HEIGHT}`}
                    className={[
                        "mx-auto block w-full max-w-[360px]",
                        disabled || submitted
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer",
                    ].join(" ")}
                    style={{
                        touchAction: "none",
                        userSelect: "none",
                    }}
                    onPointerDown={(event) => {
                        if (disabled || submitted) return;

                        draggingRef.current = true;

                        try {
                            event.currentTarget.setPointerCapture(
                                event.pointerId
                            );
                        } catch {
                            // Pointer capture is best-effort.
                        }

                        updateGaugeFromPointer(event);
                    }}
                    onPointerMove={(event) => {
                        if (!draggingRef.current) return;

                        updateGaugeFromPointer(event);
                    }}
                    onPointerUp={(event) => {
                        draggingRef.current = false;

                        try {
                            event.currentTarget.releasePointerCapture(
                                event.pointerId
                            );
                        } catch {
                            // Already released / unsupported.
                        }
                    }}
                    onPointerCancel={() => {
                        draggingRef.current = false;
                    }}
                >
                    {/* colored semicircle */}
                    {gaugeBands.map((band, index) => (
                        <path
                            key={index}
                            d={arcPath(
                                band.from,
                                band.to,
                                GAUGE_RADIUS
                            )}
                            fill="none"
                            stroke={band.stroke}
                            strokeWidth="22"
                            strokeLinecap="butt"
                        />
                    ))}

                    {/* labels 0–10 */}
                    {tickValues.map((rawTick, index) => {
                        const tickFraction =
                            index / (tickValues.length - 1);

                        const angle =
                            180 - tickFraction * 180;

                        const point =
                            pointOnGauge(angle, 133);

                        const snapped =
                            roundToStep(
                                rawTick,
                                min,
                                max,
                                step
                            );

                        return (
                            <text
                                key={index}
                                x={point.x}
                                y={point.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="11"
                                fontWeight="600"
                                fill="#475569"
                            >
                                {formatValue(snapped)}
                            </text>
                        );
                    })}

                    {/* Needle always reflects the current SUDS value. */}
                    <>
                        <line
                            x1={GAUGE_CX}
                            y1={GAUGE_CY}
                            x2={needleTip.x}
                            y2={needleTip.y}
                            stroke="#0f172a"
                            strokeWidth="5"
                            strokeLinecap="round"
                        />

                        <circle
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            r="10"
                            fill="#0f172a"
                        />

                        <circle
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            r="4"
                            fill="#ffffff"
                        />
                    </>
                </svg>
            </div>

            <div className="mt-1 flex justify-between text-xs text-slate-500">
                <span>No distress</span>
                <span>Extreme distress</span>
            </div>

            <button
                type="button"
                disabled={disabled || submitted}
                onClick={() => void submit("gauge")}
                className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
                {submitted
                    ? "Submitting…"
                    : `Submit ${formatValue(selectedValue)}`}
            </button>
        </div>
    );
};

export default SudsInput;
